// src/calendar/index.js — екран „Календар": месечен изглед за целия CRM.
//
// Календарът НЕ пази чужди събития — сървърът ги събира от регистъра на
// източниците (services/calendar.js) при всяко зареждане. Тук се рисува само
// това, което е дошло. Собствените бележки/срещи се добавят с клик върху ден.

import { el, clear, $, field, input, row } from '../shared/dom.js';
import { dateInput } from '../shared/dates.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { emptyState, panelH2, loadError } from '../shared/ui.js';
import { openModal, closeModal, confirmDialog } from '../shared/modal.js';
import { state, cq } from '../shared/state.js';
import { viewAllowed } from '../shell/shell.js';

const MONTHS = ['януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли',
  'август', 'септември', 'октомври', 'ноември', 'декември'];
const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];

// Кой вид събитие как изглежда (цветовете идват от .tag класовете).
const KINDS = {
  task: ['Задача', 'amber'],
  'invoice-due': ['Падеж на фактура', 'red'],
  'checklist-due': ['Чек лист', 'purple'],
  'vehicle-doc': ['Автопарк', 'teal'],
  note: ['Бележка', 'blue'],
  meeting: ['Среща', 'green'],
  other: ['Друго', 'gray'],
};

// Текущо показаният месец (пази се между рендерите на екрана).
let cursor = null;

// Мини-календарът на таблото отваря големия календар на конкретен месец:
// само наглася курсора — самата смяна на изгледа я прави таблото.
export function openAt(dateIso) {
  const m = String(dateIso || '').match(/^(\d{4})-(\d{2})/);
  if (m) cursor = { y: Number(m[1]), m: Number(m[2]) - 1 };
}

const pad2 = (n) => (n < 10 ? '0' : '') + n;
const iso = (y, m, d) => y + '-' + pad2(m + 1) + '-' + pad2(d);
// МЕСТНА дата, не UTC: toISOString() нощем (00:00–03:00 у нас) връща
// вчерашния ден и „днес" би светвало на грешната клетка.
const todayIso = () => { const n = new Date(); return iso(n.getFullYear(), n.getMonth(), n.getDate()); };

export async function view(page) {
  clear(page);
  if (!state().activeCompany) return window.CRM.needsCompany(page);
  if (!cursor) { const n = new Date(); cursor = { y: n.getFullYear(), m: n.getMonth() }; }

  const { y, m } = cursor;
  const first = new Date(Date.UTC(y, m, 1));
  const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  // Понеделник е първи ден от седмицата (getUTCDay: 0=неделя).
  const lead = (first.getUTCDay() + 6) % 7;
  const from = iso(y, m, 1);
  const to = iso(y, m, daysInMonth);

  let data = { holidays: [], events: [] };
  try {
    data = await api('/calendar' + cq() + '&from=' + from + '&to=' + to);
  } catch (e) { page.appendChild(loadError(() => view(page))); return; }

  const holidayBy = {};
  (data.holidays || []).forEach((h) => { holidayBy[h.date] = h; });
  const eventsBy = {};
  (data.events || []).forEach((ev) => { (eventsBy[ev.date] = eventsBy[ev.date] || []).push(ev); });

  const panel = el('div', { class: 'panel' });
  panel.appendChild(el('div', { class: 'panel-head' },
    ...panelH2('Календар'),
    el('div', { class: 'cal-nav' },
      el('button', { class: 'btn sm ghost', title: 'Предходен месец', text: '‹', onClick: () => { shift(-1); } }),
      el('span', { class: 'cal-title', text: MONTHS[m] + ' ' + y }),
      el('button', { class: 'btn sm ghost', title: 'Следващ месец', text: '›', onClick: () => { shift(1); } }),
      el('button', { class: 'btn sm ghost', text: 'Днес', onClick: () => { const n = new Date(); cursor = { y: n.getFullYear(), m: n.getMonth() }; view($('#page')); } })
    ),
    el('button', { class: 'btn sm ghost', text: '📱 Календарът в телефона ми', title: 'Показва тези срокове в Google Календар или в телефона', onClick: () => icsDialog() }),
    el('button', { class: 'btn sm', text: '+ Ново събитие', onClick: () => eventForm(null, todayInMonth(y, m)) })
  ));

  // Легенда: кой цвят какво значи.
  const legend = el('div', { class: 'cal-legend' },
    el('span', {}, el('i', { class: 'dot-nonworking' }), 'почивен / празник'),
    ...Object.keys(KINDS).filter((k) => k !== 'other').map((k) =>
      el('span', {}, el('i', { class: 'tag ' + KINDS[k][1], text: ' ' }), KINDS[k][0]))
  );
  panel.appendChild(legend);

  // Бележка, ако за СЛЕДВАЩАТА година още не са въведени разместените дни.
  const nextYear = y + 1;
  try {
    const info = await api('/calendar/holidays' + cq() + '&year=' + nextYear);
    if (!info.has_overrides) {
      // (Г.8) „Решение на МС" е жаргон за админа, който ще ги въведе;
      // всички останали получават просто обещание кой ще го свърши.
      const admin = state().user && state().user.role === 'admin';
      panel.appendChild(el('div', { class: 'cal-hint' },
        admin
          ? 'За ' + nextYear + ' още не са въведени разместени почивни дни (решение на МС).'
          : 'Почивните дни за ' + nextYear + ' г. ще бъдат въведени от администратора.',
        admin
          ? el('button', { class: 'btn sm ghost', style: 'margin-left:10px', text: 'Въведи', onClick: () => overridesForm(nextYear) })
          : null));
    }
  } catch (e) { /* бележката не е критична */ }

  const grid = el('div', { class: 'cal-grid' });
  WEEKDAYS.forEach((w) => grid.appendChild(el('div', { class: 'cal-wd', text: w })));
  for (let i = 0; i < lead; i++) grid.appendChild(el('div', { class: 'cal-cell empty' }));

  const today = todayIso();
  for (let d = 1; d <= daysInMonth; d++) {
    const date = iso(y, m, d);
    const wd = (new Date(Date.UTC(y, m, d)).getUTCDay() + 6) % 7;
    const hol = holidayBy[date];
    const weekend = wd >= 5;
    const nonworking = !!hol || weekend;

    const cell = el('div', {
      class: 'cal-cell' + (nonworking ? ' nonworking' : '') + (hol ? ' holiday' : '') + (date === today ? ' today' : ''),
      title: hol ? hol.name : '',
      // (Г.1) Кликът РАЗГЛЕЖДА деня — хората кликват да видят, а формата
      // „Ново събитие" ги стряскаше („аз нещо ли създадох?"). Създаването
      // е изричен бутон вътре в прегледа.
      onClick: () => dayDialog(date, hol, eventsBy[date] || []),
    });
    cell.appendChild(el('div', { class: 'cal-day' }, String(d),
      hol ? el('span', { class: 'cal-holiday-name', text: hol.name }) : null));

    (eventsBy[date] || []).slice(0, 4).forEach((ev) => {
      const kind = KINDS[ev.kind] ? ev.kind : (KINDS[ev.source] ? ev.source : 'other');
      cell.appendChild(el('button', {
        class: 'cal-ev ' + (ev.color || KINDS[kind][1]),
        type: 'button',
        title: (ev.time ? ev.time + ' ' : '') + ev.title + (ev.note ? ' — ' + ev.note : ''),
        onClick: (e) => { e.stopPropagation(); openEvent(ev); },
      }, (ev.time ? ev.time + ' ' : '') + ev.title));
    });
    const extra = (eventsBy[date] || []).length - 4;
    // (Г.1) „+ още N" е истински бутон към прегледа: точно скритите след
    // 4-то събития са падежите и застраховките.
    if (extra > 0) cell.appendChild(el('button', {
      class: 'cal-more', type: 'button',
      'aria-label': 'Още ' + extra + ' събития на ' + date + ' — отвори прегледа на деня',
      onClick: (e) => { e.stopPropagation(); dayDialog(date, hol, eventsBy[date] || []); },
    }, '+ още ' + extra));
    grid.appendChild(cell);
  }
  panel.appendChild(grid);

  if (!(data.events || []).length) {
    panel.appendChild(el('div', { class: 'cal-hint', text: 'През този месец няма събития. Кликнете върху ден, за да добавите бележка или среща.' }));
  }
  page.appendChild(panel);

  if (state().user && state().user.role === 'admin') page.appendChild(overridesPanel(y));
}

function shift(delta) {
  let { y, m } = cursor;
  m += delta;
  if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
  cursor = { y, m };
  view($('#page'));
}

// Ако гледаме текущия месец — днешната дата; иначе първия ден от месеца.
function todayInMonth(y, m) {
  const n = new Date();
  return (n.getFullYear() === y && n.getMonth() === m) ? todayIso() : iso(y, m, 1);
}

// (Г.1) Преглед на деня: празникът + ВСИЧКИ събития + изричното „+ Добави".
function dayDialog(date, hol, events) {
  const items = (events || []).slice();
  const body = el('div', {},
    hol ? el('div', { class: 'day-holiday', text: '🎉 ' + hol.name + ' — почивен ден' }) : null,
    items.length
      ? el('div', { class: 'day-list' }, ...items.map((ev) => {
          const kind = KINDS[ev.kind] ? ev.kind : (KINDS[ev.source] ? ev.source : 'other');
          return el('button', {
            class: 'day-ev', type: 'button',
            onClick: () => { closeModal(); openEvent(ev); },
          },
            el('span', { class: 'tag ' + (ev.color || KINDS[kind][1]), text: KINDS[kind][0] }),
            el('span', { class: 'day-ev-title', text: (ev.time ? ev.time + ' · ' : '') + ev.title }),
            ev.note ? el('span', { class: 'day-ev-note', text: ev.note }) : null);
        }))
      : el('p', { style: 'margin:0;color:var(--muted)', text: 'Няма събития за този ден.' }));
  const foot = el('div', {},
    el('button', { class: 'btn ghost', text: 'Затвори', onClick: closeModal }),
    el('button', { class: 'btn', text: '+ Добави събитие за този ден', style: 'margin-left:8px', onClick: () => { closeModal(); eventForm(null, date); } }));
  const pretty = date.split('-').reverse().join('.');
  openModal({ title: pretty, body, foot });
}

// Събитие от друг модул: отвежда към неговия екран. Собствените се редактират.
function openEvent(ev) {
  if (ev.editable) return eventForm(ev, ev.date);
  // Ако потребителят няма достъп до онзи екран, диспечерът би го изхвърлил
  // на Таблото — по-добре да му кажем какво е събитието.
  if (ev.link && ev.link.view && viewAllowed(ev.link.view)) {
    state().view = ev.link.view;
    window.CRM.renderShell();
    return;
  }
  toast(ev.title);
}

// ---- Собствени събития ----------------------------------------------------

function eventForm(existing, date) {
  const f = {
    date: dateInput((existing && existing.date) || date || todayIso()),
    time: input({ type: 'time', value: (existing && existing.time) || '' }),
    title: input({ type: 'text', value: (existing && existing.title) || '', placeholder: 'напр. Среща с клиент' }),
    note: el('textarea', { rows: 3, placeholder: 'Подробности (по желание)' }),
    kind: el('select', {}, ...['note', 'meeting', 'other'].map((k) =>
      el('option', { value: k, text: KINDS[k][0], selected: existing && existing.kind === k }))),
  };
  if (existing && existing.note) f.note.value = existing.note;

  const body = el('div', {},
    row(field('Дата', f.date), field('Час (по желание)', f.time)),
    field('Какво е събитието', f.title),
    row(field('Вид', f.kind)),
    field('Бележка', f.note)
  );

  const save = async () => {
    const payload = {
      company_id: state().activeCompany,
      date: f.date.dataset.iso,
      time: f.time.value,
      title: f.title.value.trim(),
      note: f.note.value.trim(),
      kind: f.kind.value,
      color: KINDS[f.kind.value] ? KINDS[f.kind.value][1] : 'blue',
    };
    if (!payload.date) return toast('Изберете дата.', true);
    if (!payload.title) return toast('Въведете какво е събитието.', true);
    try {
      if (existing && existing.id) await api('/calendar/events/' + existing.id, { method: 'PUT', body: JSON.stringify(payload) });
      else await api('/calendar/events', { method: 'POST', body: JSON.stringify(payload) });
      closeModal();
      toast('Записано.');
      view($('#page'));
    } catch (e) { toast(e.message, true); }
  };

  const foot = el('div', {},
    existing && existing.id ? el('button', {
      class: 'btn sm danger', text: 'Изтрий', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да изтрия ли това събитие?', { okText: 'Изтрий', danger: true }))) return;
        try { await api('/calendar/events/' + existing.id, { method: 'DELETE' }); closeModal(); toast('Изтрито.'); view($('#page')); }
        catch (e) { toast(e.message, true); }
      },
    }) : null,
    el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
    el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })
  );

  openModal({ title: existing && existing.id ? 'Събитие' : 'Ново събитие', body, foot });
}

// ---- Абонаментен линк (ICS) ----------------------------------------------

function icsDialog() {
  const box = input({ type: 'text', readonly: 'readonly', style: 'font-family:monospace;font-size:12px' });
  const fill = (url) => { box.value = location.origin + url; };
  // Стъпки 1-2-3: без тях линкът е „и сега какво?" (В.3).
  const steps = el('ol', { class: 'ics-steps' },
    el('li', {}, el('b', { text: '1. ' }), 'Копирайте линка отдолу с бутона „Копирай".'),
    el('li', {}, el('b', { text: '2. ' }), 'В Google Календар: вляво при „Други календари" натиснете „+" → „От URL адрес" и поставете линка. (На iPhone: Настройки → Календар → Акаунти → Добавяне → Абониран календар.)'),
    el('li', {}, el('b', { text: '3. ' }), 'Готово — сроковете от системата се появяват в телефона и се обновяват сами (понякога до няколко часа).'));
  const body = el('div', {},
    el('p', { style: 'margin:0 0 10px;color:var(--muted);font-size:13px' },
      'Показва сроковете от системата във вашия телефон или Google Календар. През линка НЕ може да се променя нищо в CRM-а.'),
    steps,
    field('Вашият таен линк', box),
    el('p', { style: 'margin:10px 0 0;color:var(--muted);font-size:12px' },
      'Не го споделяйте — който има линка, вижда календара. Ако е попаднал в чужди ръце, натиснете „Нов линк" и старият спира да работи.')
  );
  const foot = el('div', {},
    el('button', {
      class: 'btn sm ghost', text: 'Нов линк', style: 'margin-right:auto',
      onClick: async () => {
        if (!(await confirmDialog('Да създам ли нов линк? Старият веднага спира да работи.', { okText: 'Нов линк' }))) return;
        try { const r = await api('/calendar/ics-link/reset', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany }) }); fill(r.url); toast('Готово — добавете новия линк в календара си.'); }
        catch (e) { toast(e.message, true); }
      },
    }),
    el('button', { class: 'btn ghost', text: 'Затвори', onClick: closeModal }),
    el('button', {
      class: 'btn', text: 'Копирай', style: 'margin-left:8px',
      onClick: () => {
        box.select();
        try { document.execCommand('copy'); toast('Линкът е копиран.'); } catch (e) { toast('Маркирайте линка и го копирайте.', true); }
      },
    })
  );
  openModal({ title: 'Календарът в телефона ми', body, foot });
  api('/calendar/ics-link' + cq()).then((r) => fill(r.url)).catch((e) => { box.value = e.message; });
}

// ---- Разместени почивни дни (само админ) ---------------------------------

function overridesPanel(year) {
  const panel = el('div', { class: 'panel', style: 'margin-top:16px' });
  panel.appendChild(el('div', { class: 'panel-head' },
    el('h2', { text: 'Разместени почивни дни' }),
    el('button', { class: 'btn sm ghost', text: 'Въведи ден', onClick: () => overridesForm(year) })
  ));
  const body = el('div', {});
  panel.appendChild(el('div', { class: 'cal-hint' },
    'Министерският съвет всяка година размества някои почивни дни (например прави петък неприсъствен, а съседната събота — работна). Това не може да се изчисли и се въвежда тук.'));
  panel.appendChild(body);

  api('/calendar/overrides' + cq()).then((rows) => {
    clear(body);
    if (!rows.length) { body.appendChild(emptyState('▦', 'Още няма въведени разместени дни.')); return; }
    const tb = el('tbody');
    rows.forEach((o) => {
      tb.appendChild(el('tr', {},
        el('td', { text: o.date }),
        el('td', {}, el('span', { class: 'tag ' + (o.kind === 'working' ? 'green' : 'red'), text: o.kind === 'working' ? 'работен' : 'неприсъствен' })),
        el('td', { text: o.reason || '' }),
        el('td', { class: 'row-actions' }, el('button', {
          class: 'btn sm danger', text: 'Изтрий',
          onClick: async () => {
            if (!(await confirmDialog('Да махна ли този разместен ден?', { okText: 'Изтрий', danger: true }))) return;
            try { await api('/calendar/overrides/' + o.id, { method: 'DELETE' }); toast('Изтрито.'); view($('#page')); }
            catch (e) { toast(e.message, true); }
          },
        }))
      ));
    });
    body.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { text: 'Дата' }), el('th', { text: 'Вид' }), el('th', { text: 'Причина' }), el('th', { text: '' }))), tb));
  }).catch((e) => { clear(body); body.appendChild(el('div', { class: 'cal-hint', text: e.message })); });

  return panel;
}

function overridesForm(year) {
  const f = {
    date: dateInput(year + '-01-01'),
    kind: el('select', {},
      el('option', { value: 'nonworking', text: 'Неприсъствен (почивен) ден' }),
      el('option', { value: 'working', text: 'Работен ден (отработване)' })),
    reason: input({ type: 'text', placeholder: 'напр. РМС № 123 от 2026 г.' }),
  };
  const save = async () => {
    if (!f.date.dataset.iso) return toast('Изберете дата.', true);
    try {
      await api('/calendar/overrides', {
        method: 'POST',
        body: JSON.stringify({ company_id: state().activeCompany, date: f.date.dataset.iso, kind: f.kind.value, reason: f.reason.value.trim() }),
      });
      closeModal(); toast('Записано.'); view($('#page'));
    } catch (e) { toast(e.message, true); }
  };
  openModal({
    title: 'Разместен почивен ден',
    body: el('div', {}, field('Дата', f.date), field('Вид', f.kind), field('Причина / основание', f.reason)),
    foot: el('div', {},
      el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }),
      el('button', { class: 'btn', text: 'Запази', style: 'margin-left:8px', onClick: save })),
  });
}
